import { Storage as AmplifyStorage } from "aws-amplify";

import { REACT_APP_REGION } from "@ds/constants/environment";

export interface StorageServiceConfig {
  region: string;
  bucket?: string;
}

export interface StorageMethodConfig {
  level?: "public" | "protected" | "private";
  contentType?: string;
  download?: boolean;
  maxKeys?: number;
  progressCallback?: (progressInfo: ProgressInfo) => void;
}

export interface ProgressInfo {
  key?: string;
  name?: string;
  loaded?: number;
  part?: number;
  total?: number;
  isTrusted?: boolean;
}

class StorageService {
  private config: StorageServiceConfig = {
    region: REACT_APP_REGION,
  };

  private pendingOperations = new Set<Promise<unknown>>();

  private track<T>(p: Promise<T>): Promise<T> {
    this.pendingOperations.add(p);
    void p.finally(() => this.pendingOperations.delete(p));
    return p;
  }

  cancelPending() {
    // AmplifyStorage.cancel(promise) is a valid Amplify v5 Storage API (Storage.d.ts overload 2).
    this.pendingOperations.forEach(p => AmplifyStorage.cancel(p, "Operation cancelled on logout"));
    this.pendingOperations.clear();
  }

  configure(cfg: StorageServiceConfig) {
    this.config = { ...this.config, ...cfg };
    AmplifyStorage.configure({
      bucket: this.config.bucket,
      region: this.config.region,
    });
  }

  /**
   * Clears the AWSS3Provider's cached credentials before each request. Setting credentials to null
   * forces loadS3Config to use credentialsProvider instead of a stale static snapshot — credentialsProvider
   * calls Credentials.get() on every request, which detects expiry and exchanges the current Cognito
   * session for fresh IAM credentials automatically. This covers the case where a user leaves the
   * browser tab open long enough for the cached credentials to expire.
   */
  private refreshCredentials() {
    AmplifyStorage.configure({ AWSS3: { credentials: null } });
  }

  async get(key: string, config?: StorageMethodConfig) {
    this.refreshCredentials();
    return this.track(AmplifyStorage.get(key, config) as Promise<string | { Body: Uint8Array; ContentType: string }>);
  }

  async put(key: string, data: unknown, config?: StorageMethodConfig) {
    this.refreshCredentials();
    return this.track(AmplifyStorage.put(key, data, config) as unknown as Promise<{ key: string }>);
  }

  async remove(key: string, config?: StorageMethodConfig): Promise<unknown> {
    this.refreshCredentials();
    return this.track(AmplifyStorage.remove(key, config) as Promise<unknown>);
  }

  async list(key: string, config?: StorageMethodConfig): Promise<unknown> {
    this.refreshCredentials();
    return this.track(AmplifyStorage.list(key, config) as Promise<unknown>);
  }
}

export const storageService = new StorageService();
